// EDV Next 0.11.0 - pure visual helpers for the FMEA workspace (ported from
// the prototype design contract; NO JSX, no React import so it is testable
// directly with node --experimental-strip-types).

export const SOD_STRONG = [
  null, // index 0
  { bg: '#43a047', fg: '#fff',    border: '#388e3c' },
  { bg: '#66bb6a', fg: '#fff',    border: '#5aa85e' },
  { bg: '#9ccc65', fg: '#1f3a05', border: '#8bbc55' },
  { bg: '#d4e157', fg: '#3a4a05', border: '#c0cf48' },
  { bg: '#ffee58', fg: '#5d4a00', border: '#efd946' },
  { bg: '#ffca28', fg: '#4a3900', border: '#efb716' },
  { bg: '#ffa726', fg: '#fff',    border: '#ef9711' },
  { bg: '#ff7043', fg: '#fff',    border: '#f0602c' },
  { bg: '#ef5350', fg: '#fff',    border: '#dc3f3c' },
  { bg: '#c62828', fg: '#fff',    border: '#b71c1c' },
] as const;

export function sodBadgeStyle(v: number | null | undefined): {
  bg: string; fg: string; border: string; empty: boolean;
} {
  if (v === null || v === undefined || v === 0) {
    return { bg: 'transparent', fg: '#889', border: '#c0cbd6', empty: true };
  }
  const n = Math.max(1, Math.min(10, Math.trunc(v)));
  const p = SOD_STRONG[n]!;
  return { bg: p.bg, fg: p.fg, border: p.border, empty: false };
}

export function rpn(s: number | null, o: number | null, d: number | null): number | null {
  if (s == null || o == null || d == null || s === 0 || o === 0 || d === 0) return null;
  return s * o * d;
}

export type RpnBucket = 'none' | 'low' | 'med' | 'high';
export function rpnBucket(r: number | null): RpnBucket {
  if (r === null) return 'none';
  if (r >= 200)  return 'high';
  if (r >= 100)  return 'med';
  return 'low';
}
export function rpnBucketStyle(b: RpnBucket): { bg: string; fg: string; border: string } {
  switch (b) {
    case 'high': return { bg: '#ffebee', fg: '#c62828', border: '#ef9a9a' };
    case 'med':  return { bg: '#fff3e0', fg: '#e65100', border: '#ffcc80' };
    case 'low':  return { bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' };
    case 'none': return { bg: 'transparent', fg: '#889', border: '#c0cbd6' };
  }
}

// Action-status palette (matches ActionStatus in fmea-api).
export const ACTION_STATUS_META = {
  open:          { label: 'Open',        fg: '#c62828', bg: '#ffebee' },
  'in-progress': { label: 'In progress', fg: '#e67e22', bg: '#fff3e0' },
  blocked:       { label: 'Blocked',     fg: '#7a5f00', bg: '#fff8e1' },
  completed:     { label: 'Completed',   fg: '#2e7d32', bg: '#e8f5e9' },
  cancelled:     { label: 'Cancelled',   fg: '#889',    bg: '#f0f4f8' },
} as const;

// Full-text default S/O/D scales from the prototype (owner-editable in future
// releases; hardcoded here so the FMEA cell tooltips read useful text).
export const SEV_SCALE: readonly string[] = [
  '', // 0
  'No Effect',
  'Minor Effect - no intervention required',
  'Minor Effect - minor intervention required but without performance impact',
  'Minor Effect - significant intervention required but without performance impact',
  'Moderate Effect - reduction of secondary functional performance',
  'Moderate Effect - loss of secondary function',
  'Major Effect - reduction of primary functional performance',
  'Major Effect - loss of primary function',
  'Catastrophic Effect - hazardous functional failure with warning',
  'Catastrophic Effect - hazardous functional failure without warning',
];
export const OCC_SCALE: readonly string[] = [
  '',
  'Remote - failure is unlikely (> 7 years or 1 in 6000)',
  'Very Low - only isolated failures with identical processes (3 to 6 years or 1 in 3000)',
  'Low - isolated failures with similar processes (1 to 3 years or 1 in 1500)',
  'Moderate - occasional failures in similar processes (1 year or 1 in 800)',
  'Moderate - occasional failures in similar processes (6 months or 1 in 400)',
  'Moderate - occasional failures in similar processes (3 months or 1 in 80)',
  'High - repeated failures in similar processes (monthly or 1 in 20)',
  'High - repeated failures in similar processes (weekly or 1 in 8)',
  'Very High - failure is almost inevitable (every 3 to 4 days or 1 in 3)',
  'Very High - failure is almost inevitable (more than once per day or 1 in 1)',
];
export const DET_SCALE: readonly string[] = [
  '',
  'Almost Certain - design/inspection will almost always detect',
  'Very High - very high chance the control will detect',
  'High - high chance the control will detect',
  'Moderately High - moderately high chance the control will detect',
  'Moderate - moderate chance the control will detect',
  'Low - low chance the control will detect',
  'Very Low - very low chance the control will detect',
  'Remote - remote chance the control will detect',
  'Very Remote - very remote chance the control will detect',
  'Almost Impossible - no known control available to detect',
];
